import { ComponentType } from 'react';
import { SetupPackageImportButton } from '@/core/SetupPackageImportButton';
import { AssignUserRolesButton } from '@/core/AssignUserRolesButton';
import { DuplicateRoleButton } from '@/core/DuplicateRoleButton';
import { LogInAsUserButton } from '@/core/LogInAsUserButton';
import { AccountRuleOrderButton } from '@/finance/AccountRuleOrderButton';
import { ReevaluatePlanHeaderLinesButton } from '@/finance/ReevaluatePlanHeaderLinesButton';
import { NewJobFromMessageTypeButton } from '@/integration/NewJobFromMessageTypeButton';
import { SaveIntegrationSetupButton } from '@/integration/SaveIntegrationSetupButton';

/**
 * Bespoke (non-DSL) toolbar actions rendered on an entity's generated List View, keyed by entity
 * name. The hand-written <EntityListView> shell renders `<ListViewExtras entity="X" selectedIds={…} />`
 * after the built-in New/bulk buttons; add an entry here to surface a hand-written grid action
 * without editing generated files or the generator.
 *
 * Third sibling of the two detail-view registries:
 *   - menuActionExtras   → the row's Actions menu, on the DETAIL view
 *   - detailViewExtras   → banners/panels, on the DETAIL view
 *   - listViewExtras     → toolbar actions, on the LIST view  ← this one
 *
 * The gap this fills: both other registries only reach a record you have already opened. An action
 * that answers a question ABOUT the list ("in what order are these evaluated?") belongs on the list,
 * and an entity with no workflow or commands never gets an Actions menu generated at all — so for
 * those entities this is the only hand-written entry point that exists.
 *
 * Extras receive the current selection so an action can act on, or scope itself to, picked rows.
 */

export interface ListViewExtraProps {
  /** The entity the list shows — set by the shell, for an extra registered on several lists. */
  entity?: string;
  /** Ids currently selected in the grid; empty when nothing is picked. */
  selectedIds: string[];
  /**
   * The selected rows as the grid currently holds them, so an action can read row fields (e.g.
   * `status`) without a round-trip. May be shorter than `selectedIds` when a selected row is no
   * longer loaded — `selectedIds` stays the authority on WHAT was picked.
   */
  selectedRows: Record<string, unknown>[];
  /**
   * Tell the list that the action changed data: the shell clears the selection and refetches the
   * grid (its Refresh button). Optional so read-only extras need not care.
   */
  onActionCompleted?: () => void;
}

const registry: Record<string, ComponentType<ListViewExtraProps>[]> = {
  AccountDerivationRule: [AccountRuleOrderButton],
  // Each list of the integration setup packages the rows selected in it.
  IntegrationClient: [SaveIntegrationSetupButton],
  IntegrationConnection: [SaveIntegrationSetupButton],
  IntegrationJob: [NewJobFromMessageTypeButton, SaveIntegrationSetupButton],
  PlanHeader: [ReevaluatePlanHeaderLinesButton],
  Role: [DuplicateRoleButton],
  SetupPackage: [SetupPackageImportButton],
  User: [AssignUserRolesButton, LogInAsUserButton],
};

export function ListViewExtras({
  entity,
  selectedIds,
  selectedRows,
  onActionCompleted,
}: { entity: string } & ListViewExtraProps) {
  const extras = registry[entity];
  if (!extras || extras.length === 0) return null;
  return (
    <>
      {extras.map((Extra, index) => (
        <Extra
          key={index}
          entity={entity}
          selectedIds={selectedIds}
          selectedRows={selectedRows}
          onActionCompleted={onActionCompleted}
        />
      ))}
    </>
  );
}
